import React from 'react';

// ==================== ENUMS ====================

export enum UserRole {
    EMPLOYER = 'employer',
    AGENT = 'agent',
    HR = 'hr',
    SUPER_ADMIN = 'super_admin',
    // Assigns agents & medical users, follows up, owns guarantor reassignment.
    CUSTOMER_SUCCESS = 'customer_success',
    // Enters the medical report for investigations assigned to them.
    MEDICAL = 'medical',
    // Reviews the employee's signed affidavit and uploads the notarised copy.
    NOTARY = 'notary',
    // Downloads the employee profile and uploads the police character certificate.
    POLICE = 'police',
}

// Drives the required medical test panel. Defaults to `employment`.
export enum InvestigationCategory {
    DOMESTIC_STAFF = 'domestic_staff',
    EMPLOYMENT = 'employment',
}

export enum MedicalReportStatus {
    SUBMITTED = 'submitted',
}

export enum UserStatus {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    SUSPENDED = 'SUSPENDED',
}

export enum InvestigationStatus {
    NEW = 'new',
    PENDING = 'pending',
    AWAITING_APPROVAL = 'awaiting_approval',
    IN_PROGRESS = 'in_progress',
    UNDER_REVIEW = 'under_review',
    COMPLETED = 'completed',
    CANCELLED = 'cancelled',
}

export enum CheckType {
    STANDARD = 'standard',
    ENHANCED = 'enhanced',
    PRO = 'pro',
}

export enum InvestigationState {
    DETAILS_SUBMITTED = 'details_submitted',
    INVESTIGATION_REPORT = 'investigation_report',
}

export enum PaymentStatus {
    PENDING = 'PENDING',
    SUCCESSFUL = 'SUCCESSFUL',
    FAILED = 'FAILED',
    REFUNDED = 'REFUNDED',
}

export enum DocumentType {
    CV = 'cv',
    NATIONAL_ID = 'national_id',
    IBIN = 'nin',
    PASSPORT = 'passport',
    LETTER_OF_EMPLOYMENT = 'letter_of_employment',
    AGENT_REPORT = 'agent_report',
    INVESTIGATION_REPORT = 'investigation_report',
    SIGNED_AFFIDAVIT = 'signed_affidavit',
    NOTARIZED_AFFIDAVIT = 'notarized_affidavit',
    POLICE_CHARACTER_CERTIFICATE = 'police_character_certificate',
    SOCIAL_MEDIA_REPORT = 'social_media_report',
    OTHER = 'other',
}

export enum DocumentApprovalStatus {
    AWAITING_APPROVAL = 'awaiting_approval',
    APPROVED = 'approved',
    REJECTED = 'rejected',
    COMPLETED = 'completed',
}

export enum DocumentUploader {
    EMPLOYEE = 'employee',
    AGENT = 'agent',
}

export enum RiskLevel {
    LOW = 'low',
    MODERATE = 'moderate',
    HIGH = 'high',
    SEVERE = 'severe',
    CRITICAL = 'critical',
}

export enum RiskRecommendation {
    PROCEED = 'proceed',
    PROCEED_WITH_CONDITIONS = 'proceed_with_conditions',
    ESCALATE_BOARD_REVIEW = 'escalate_board_review',
    DO_NOT_PROCEED = 'do_not_proceed',
}

export enum RiskCategory {
    IDENTITY_INTEGRITY = 'identity_integrity',
    CRIMINAL_ANTI_CORRUPTION = 'criminal_anti_corruption',
    FINANCIAL_DIRECTORSHIP = 'financial_directorship',
    REGULATORY_DISCIPLINE = 'regulatory_discipline',
    POLITICAL_EXPOSURE = 'political_exposure',
    LITIGATION_HISTORY = 'litigation_history',
    SAFEGUARDING_CONDUCT = 'safeguarding_conduct',
    REPUTATIONAL_MEDIA = 'reputational_media',
    CONFLICT_OF_INTEREST = 'conflict_of_interest',
    GUARANTOR_RELIABILITY = 'guarantor_reliability',
}

export enum EarningStatus {
    PENDING = 'PENDING',
    PAID = 'PAID',
}

export enum WaitlistStatus {
    PENDING = 'pending',
    CONTACTED = 'contacted',
    CONVERTED = 'converted',
}

export enum TicketStatus {
    OPEN = 'open',
    IN_PROGRESS = 'in_progress',
    WAITING_ON_EMPLOYEE = 'waiting_on_employee',
    RESOLVED = 'resolved',
    CLOSED = 'closed',
}

export enum TicketPriority {
    LOW = 'low',
    MEDIUM = 'medium',
    HIGH = 'high',
    URGENT = 'urgent',
}

export enum TicketCategory {
    TECHNICAL = 'technical',
    BILLING = 'billing',
    ACCOUNT = 'account',
    INVESTIGATION = 'investigation',
    GENERAL = 'general',
    OTHER = 'other',
}

// ==================== USER TYPES ====================

export interface User {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    role: UserRole;
    status: UserStatus;
    isPhoneVerified: boolean;
    isEmailVerified: boolean;
    companyName?: string;
    country?: string;
    state?: string;
    city?: string;
    gender?: 'male' | 'female' | 'other';
    // Referral program fields (HR-only on HR users, employer-only for the others)
    referralCode?: string | null;
    referredById?: string | null;
    referralAttributedAt?: string | null;
    referralActive?: boolean;
    // Admin-controlled: employer accounts flagged complimentary skip payment.
    paymentExempt?: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface UserStatistics {
    totalInvestigations: number;
    completedInvestigations: number;
    totalSpent?: number; // for Employer
    totalEarnings?: number; // for Agent
}

export interface UserWithStats extends User {
    statistics: UserStatistics;
}

// ==================== AUTH TYPES ====================

export interface LoginRequest {
    email?: string;
    phoneNumber?: string;
}

export interface LoginResponse {
    success: boolean;
    message: string;
    phoneNumber: string;
    email: string;
}

export interface LoginVerifyRequest {
    email?: string;
    phoneNumber?: string;
    otp: string;
}

export interface LoginVerifyResponse {
    access_token: string;
    refresh_token: string;
    user: User;
}

export interface RegisterRequest {
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    role: UserRole;
    companyName?: string;
    country?: string;
    state?: string;
    city?: string;
    gender?: 'male' | 'female' | 'other';
    // Only consumed when role === 'employer'. Invalid/expired codes are silently dropped server-side.
    referralCode?: string;
}

export interface RegisterResponse {
    message: string;
    user: User;
    requiresPhoneVerification: boolean;
}

export interface RefreshTokenRequest {
    refreshToken: string;
}

export interface RefreshTokenResponse {
    access_token: string;
    refresh_token: string;
}

export interface SendOtpRequest {
    phoneNumber: string;
}

export interface SendOtpResponse {
    message: string;
    expiresIn: number;
}

export interface VerifyOtpRequest {
    phoneNumber: string;
    otp: string;
}

export interface VerifyOtpResponse {
    success: boolean;
    message: string;
    access_token: string;
    refresh_token: string;
    user: User;
}

// ==================== INVESTIGATION TYPES ====================

export interface Investigation {
    id: string;
    employeeFirstName: string;
    employeeMiddleName?: string;
    employeeLastName: string;
    employeeEmail: string;
    employeePhone: string;
    employeeGender?: string;
    checkType: CheckType;
    status: InvestigationStatus;
    requestLink: string;
    supportCode?: string;
    isFormSubmitted: boolean;
    employeeConsentGiven?: boolean;
    dateConsentGiven?: string;
    dateOfBirth?: string;
    nationality?: string;
    stateOfOrigin?: string;
    localGovernmentArea?: string;
    usa?: string;
    iin?: string;
    dateRequested?: string;
    dateSubmitted?: string;
    reportSubmittedDate?: string;
    reportTitle?: string;
    executiveSummary?: string;
    reportDetails?: string;
    // Legacy fields retained so historical reports keep rendering
    investigationNotes?: string;
    reportUrl?: string;
    riskScore?: number;
    // Questionnaire Step 1: Personal Identification
    fullLegalName?: string;
    previousNames?: string;
    alias?: string;
    placeOfBirth?: string;
    otherNationalities?: string;
    nationalIdentificationNumber?: string;
    addressHistory?: string; // JSON string
    workHistory?: string; // JSON string
    educationHistory?: string; // JSON string
    // Questionnaire Step 2: Criminal & Law Enforcement
    hasConviction?: string;
    convictionDetails?: string;
    isUnderInvestigation?: string;
    investigationDetails?: string;
    hasFinancialCrimeCharge?: string;
    financialCrimeDetails?: string;
    hasPleaBargain?: string;
    pleaBargainDetails?: string;
    hasBeenDisengaged?: string;
    disengagementDetails?: string;
    // Questionnaire Step 3: Financial & Directorship
    hasLiquidatedCompany?: string;
    liquidatedCompanyDetails?: string;
    hasDirectorDisqualification?: string;
    directorDisqualificationDetails?: string;
    isCurrentDirector?: string;
    currentDirectorshipDetails?: string;
    // Questionnaire Step 4: Regulatory & Professional Discipline
    hasDisciplinaryProceedings?: string;
    disciplinaryProceedingsDetails?: string;
    hasLicenceRevoked?: string;
    licenceRevokedDetails?: string;
    revokedLicences?: string; // JSON string
    hasRegulatoryBodySanction?: string;
    regulatoryBodySanctionDetails?: string;
    // Questionnaire Step 5: Political Exposure
    isPep?: string;
    hasHeldPublicOffice?: string;
    publicOfficeDetails?: string;
    isRelatedToPep?: string;
    hasProcurementInvestigation?: string;
    // Questionnaire Step 6: Safeguarding & Workplace Conduct
    hasMisconductDismissal?: string;
    misconductDismissalDetails?: string;
    hasWorkplaceInvestigation?: string;
    hasWorkedWithVulnerableGroups?: string;
    hasLeadershipBar?: string;
    leadershipBarDetails?: string;
    // Questionnaire Step 7: Litigation & Civil Claims
    hasShareholderDisputes?: string;
    hasJudgments?: string;
    // Questionnaire Step 8: Reputational & Media
    hasAdverseMedia?: string;
    adverseMediaDetails?: string;
    hasPublicAccusation?: string;
    hasUndisclosedReputationalMatters?: string;
    // Questionnaire Step 9: Conflict of Interest
    hasBusinessConflict?: string;
    businessConflictDetails?: string;
    hasProcurementRelationship?: string;
    procurementRelationshipDetails?: string;
    hasCompetingBoard?: string;
    competingBoardDetails?: string;
    // Consent fields
    consentCriminalBackground?: boolean;
    consentEmploymentReference?: boolean;
    consentGuarantorReference?: boolean;
    consentAddressVerification?: boolean;
    consentSocialMediaReview?: boolean;
    consentShareMedicalWithEmployer?: boolean;
    consentCourtRecordSearches?: boolean;
    consentLifestyleAudit?: boolean;
    consentProcurementIntegrityChecks?: boolean;
    // Risk Assessment results
    totalRiskScore?: number;
    maxRiskScore?: number;
    riskScorePercentage?: number;
    riskLevel?: RiskLevel;
    riskRecommendation?: RiskRecommendation;
    autoRejectReason?: string;
    riskAssessments?: RiskCategoryScore[];
    riskAssessmentVersion?: number;
    lastRiskAssessmentAt?: string | null;
    isReportValidated?: boolean | null;
    reportValidatedBy?: string;
    reportValidatedAt?: string;
    validationNotes?: string | null;
    // Set by the admin report-edit endpoint (PATCH /investigations/:id/report).
    // Surfaces as "Last edited by Admin at …" in the report edit UI.
    reportLastEditedBy?: string | null;
    reportLastEditedAt?: string | null;
    earningCreated?: boolean;
    earningId?: string;
    isPaid?: boolean;
    // Audit marker for free (payment-exempt) investigations — amountPaid is 0.
    isComplimentary?: boolean;
    amountPaid?: number;
    serviceCharge?: number;
    // SLA & deadline fields (set per stage by the backend; null outside that stage)
    assignmentDeadline?: string | null;
    assignmentOverdueNotifiedAt?: string | null;
    fieldworkDeadline?: string | null;
    fieldworkReminderSentAt?: string | null;
    fieldworkOverdueNotifiedAt?: string | null;
    reviewDeadline?: string | null;
    reviewOverdueNotifiedAt?: string | null;
    employerId: string;
    agentId?: string;
    employer?: User;
    agent?: User;
    documents?: InvestigationDocument[];
    earning?: Earning;
    // Customer Success / Medical add-on fields
    investigationCategory?: InvestigationCategory;
    medicalAnalysisIncluded?: boolean;
    // Add-on ids bought with this check (snapshot taken at checkout).
    addons?: string[] | null;
    // Plan features actually bought. Shorter than the plan's list means the
    // employer cherry picked and paid the markup.
    featureIds?: string[] | null;
    socialMediaHandles?: SocialMediaHandles | null;
    customerSuccessId?: string | null;
    medicalUserId?: string | null;
    customerSuccess?: User | null;
    medicalUser?: User | null;
    // Assigned notary + when they uploaded the notarised affidavit (null until done).
    notaryUser?: User | null;
    notaryCompletedAt?: string | null;
    // Assigned police user + when they uploaded the character certificate (null until done).
    policeUser?: User | null;
    policeCompletedAt?: string | null;
    // Assigned medical lab (only the id is embedded; resolve the lab via /medical-labs).
    medicalLabId?: string | null;
    medicalLabAssignedAt?: string | null;
    medicalLabAssignedById?: string | null;
    // Guarantor investigation fields
    employmentTown?: string | null;
    guarantorTownWarning?: boolean;
    guarantors?: InvestigationGuarantor[];
    // Report turnaround window — stamped when payment succeeds, null until then.
    expectedReportReadyFrom?: string | null;
    expectedReportReadyBy?: string | null;
    createdAt: string;
    updatedAt: string;
}

// ==================== GUARANTORS ====================

export enum GuarantorStatus {
    PENDING = 'pending',
    IN_PROGRESS = 'in_progress',
    VERIFIED = 'verified',
    FAILED = 'failed',
}

export interface InvestigationGuarantor {
    id: string;
    investigationId: string;
    fullName: string;
    // Optional — drives the email notification (the SMS-only path is a bare link).
    email?: string | null;
    phone?: string;
    address?: string;
    town?: string;
    state?: string;
    relationship?: string;
    withinEmploymentTown: boolean;
    assignedAgentId?: string | null;
    assignedAgent?: User | null;
    status: GuarantorStatus;
    verificationNotes?: string | null;
    earningShareAmount?: number | null;
    reassigned?: boolean;
    reassignedFromAgentId?: string | null;
    reassignedAt?: string | null;
    // Guarantor recommendation. `recommendationToken` is deliberately NOT exposed
    // by the API (it's a credential), so the link can't be rebuilt from this payload.
    recommendationRequestedAt?: string | null;
    relationshipConfirmed?: boolean | null;
    recommendation?: string | null;
    recommendationSubmittedAt?: string | null;
    /** The signed indemnity form. `indemnity.submittedAt === null` = not completed. */
    indemnity?: GuarantorIndemnity | null;
    createdAt?: string;
    updatedAt?: string;
}

// Single guarantor as submitted by the employee (public, token-based flow).
export interface GuarantorInput {
    fullName: string;
    // Optional, but must be a valid email if sent — omit the key entirely when blank.
    email?: string;
    phone?: string;
    address?: string;
    town?: string;
    state?: string;
    relationship?: string;
}

// ==================== GUARANTOR RECOMMENDATION (public, by token) ====================

// GET /investigations/guarantor-recommendation/:token
export interface GuarantorRecommendationRequest {
    guarantorName: string;
    employeeName: string;
    companyName: string;
    relationship?: string | null;
    alreadySubmitted: boolean;
    relationshipConfirmed?: boolean | null;
    recommendation?: string | null;
    submittedAt?: string | null;
}

// POST /investigations/guarantor-recommendation/:token — send ONLY these two keys.
export interface SubmitGuarantorRecommendationRequest {
    token: string;
    relationshipConfirmed: boolean;
    recommendation: string;
}

export interface SubmitGuarantorRecommendationResponse {
    guarantorName: string;
    relationshipConfirmed: boolean;
    recommendation: string;
    submittedAt: string;
}

// Server-enforced bounds on the recommendation text — mirrored client-side.
export const RECOMMENDATION_MIN_LENGTH = 10;
export const RECOMMENDATION_MAX_LENGTH = 2000;

// ==================== GUARANTOR INDEMNITY FORM (public, by token) ====================

export type MeansOfIdentification = 'nin' | 'drivers_licence' | 'international_passport' | 'voters_card' | 'other';

/** Send the value, show the label. */
export const MEANS_OF_IDENTIFICATION_OPTIONS: { value: MeansOfIdentification; label: string }[] = [
    { value: 'nin', label: 'National Identification Number' },
    { value: 'drivers_licence', label: "Driver's licence" },
    { value: 'international_passport', label: 'International passport' },
    { value: 'voters_card', label: "Voter's card" },
    { value: 'other', label: 'Other' },
];

/**
 * A legal clause as returned by the API.
 *
 * Rendered from the response and never hardcoded: each submission records the
 * `formVersion` it was signed under, so if the client shipped its own copy of
 * the wording, the version stamped in the database would stop matching what the
 * guarantor actually read — and the indemnity becomes hard to rely on.
 */
export interface IndemnityClause {
    key: string;
    heading: string;
    paragraphs: string[];
}

/** Section 1 — read-only context about the person being vouched for. */
export interface IndemnityApplicant {
    fullName: string;
    organisation: string | null;
    /**
     * Always null today: the role a candidate applied for isn't stored on the
     * investigation. Render the slot blank or omit the phrase — never invent it
     * and never ask the guarantor for it.
     */
    roleAppliedFor: string | null;
}

/** Section 2 defaults. Nulls are the six new fields on a first visit. */
export interface IndemnityPrefill {
    fullName: string | null;
    residentialAddress: string | null;
    phoneNumber: string | null;
    email: string | null;
    occupation: string | null;
    employerBusinessName: string | null;
    officeAddress: string | null;
    relationshipWithApplicant: string | null;
    periodKnown: string | null;
    meansOfIdentification: MeansOfIdentification | null;
    identificationNumber: string | null;
    recommendation: string | null;
}

// GET /investigations/guarantor-indemnity/:token
export interface GuarantorIndemnityForm {
    applicant: IndemnityApplicant;
    prefill: IndemnityPrefill;
    formVersion: string;
    clauses: IndemnityClause[];
    alreadySubmitted: boolean;
    submittedAt: string | null;
}

/**
 * POST body — exactly these sixteen keys and nothing else.
 *
 * The API runs a global whitelist with `forbidNonWhitelisted`, so a stray
 * `token`, a leftover `relationshipConfirmed` or a UI-only `agreeAll` is a 400
 * naming the offending property. Build this object explicitly rather than
 * spreading form state.
 */
export interface SubmitGuarantorIndemnityRequest {
    fullName: string;
    residentialAddress: string;
    phoneNumber: string;
    email: string;
    occupation: string;
    employerBusinessName: string;
    officeAddress: string;
    relationshipWithApplicant: string;
    periodKnown: string;
    meansOfIdentification: MeansOfIdentification;
    identificationNumber: string;
    indemnityAccepted: boolean;
    verificationConsentAccepted: boolean;
    recommendation: string;
    declarationAccepted: boolean;
    signatureName: string;
}

export interface SubmitGuarantorIndemnityResponse {
    guarantorName: string;
    formVersion: string;
    submittedAt: string;
    recommendation: string;
}

/** Server-enforced field bounds, mirrored so the guarantor isn't bounced. */
export const INDEMNITY_MAX_LENGTHS = {
    fullName: 200,
    residentialAddress: 500,
    phoneNumber: 20,
    email: 255,
    occupation: 200,
    employerBusinessName: 200,
    officeAddress: 500,
    relationshipWithApplicant: 200,
    periodKnown: 100,
    identificationNumber: 100,
} as const;

/**
 * The signed indemnity, exposed on every guarantor in the admin/agent listings.
 *
 * Branch on `submittedAt` — set only when all three acceptances were given and
 * the form was signed. `null` means the guarantor hasn't completed it.
 * `ipAddress`/`userAgent` are evidence of execution: details drawer, not a table
 * column.
 */
export interface GuarantorIndemnity {
    submittedAt: string | null;
    formVersion: string;
    occupation: string | null;
    employerBusinessName: string | null;
    officeAddress: string | null;
    periodKnown: string | null;
    meansOfIdentification: MeansOfIdentification | null;
    identificationNumber: string | null;
    indemnityAccepted: boolean;
    verificationConsentAccepted: boolean;
    declarationAccepted: boolean;
    signatureName: string | null;
    ipAddress: string | null;
    userAgent: string | null;
}

export interface SubmitGuarantorsRequest {
    linkToken: string;
    guarantors: GuarantorInput[];
}

export interface SubmitGuarantorsResponse {
    guarantors: InvestigationGuarantor[];
    employmentTown?: string | null;
    guarantorTownWarning: boolean;
}

// Lightweight agent shape returned by the available-agents endpoint.
export interface AvailableAgent {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    city?: string;
    state?: string;
}

export interface UpdateGuarantorVerificationRequest {
    status?: GuarantorStatus;
    verificationNotes?: string;
}

// ==================== SLA / TIMER ====================

export type SlaStage = 'assignment' | 'fieldwork' | 'review';
export type SlaTimerStatus = 'on_track' | 'urgent' | 'overdue' | 'inactive';
export type SlaResponsibleParty = 'super_admin' | 'agent';

export interface InvestigationTimer {
    investigationId: string;
    status: InvestigationStatus;
    stage: SlaStage | null;
    responsibleParty: SlaResponsibleParty | null;
    startedAt: string | null;
    deadline: string | null;
    reminderAt: string | null;
    durationHours: number | null;
    remainingSeconds: number | null;
    elapsedSeconds: number | null;
    timerStatus: SlaTimerStatus;
    serverTime: string;
    message: string;
}

export interface SlaStageCounts {
    active: number;
    dueSoon: number;
    overdue: number;
}

export interface SlaFieldworkCounts extends SlaStageCounts {
    reminderPending: number;
}

export interface SlaSummary {
    serverTime: string;
    assignment: SlaStageCounts;
    fieldwork: SlaFieldworkCounts;
    review: SlaStageCounts;
}

// Employee social-media handles captured at investigation creation. Every field
// is optional — the employer fills in whatever they have.
export interface SocialMediaHandles {
    instagram?: string;
    twitter?: string;
    facebook?: string;
    linkedin?: string;
    tiktok?: string;
    other?: string;
}

export interface CreateInvestigationRequest {
    checkType: CheckType;
    employeeFirstName: string;
    employeeMiddleName?: string;
    employeeLastName: string;
    employeeEmail: string;
    employeePhone: string;
    employeeGender?: string;
    transactionId?: string; // Optional - link to existing successful payment for payment-first flow
    referenceNumber?: string;
    description?: string;
    urgencyLevel?: 'low' | 'medium' | 'high';
    requiresBackgroundCheck?: boolean;
    // Medical / Customer Success add-on fields
    investigationCategory?: InvestigationCategory;
    medicalAnalysisIncluded?: boolean;
    // Extras bought on top of the plan, at face value.
    addons?: string[];
    // Plan features being bought. Omit for the whole plan.
    features?: string[];
    // NOTE: socialMediaHandles is no longer sent on employer create — the employee
    // now provides handles on the consent form (see SubmitEmployeeFormRequest).
}

// Server returns the Investigation flat (with `requestLink` as a sibling field on
// the Investigation interface itself). The baseQuery unwraps `{ success, data, timestamp }`
// before RTK Query sees it, so `mutation.data` is the Investigation directly.
export type CreateInvestigationResponse = Investigation;

// POST /investigations/validate. `requiresPayment` is false for payment-exempt
// (complimentary) employer accounts — branch on it to skip the payment screen.
export interface ValidateInvestigationResponse {
    valid: boolean;
    message: string;
    amount: number;
    checkType: string;
    paymentExempt?: boolean;
    requiresPayment?: boolean;
    amountDue?: number;
}

// PATCH /admin/users/:id/payment-exemption
export interface PaymentExemptionRequest {
    exempt: boolean;
}

// Admin-editable plan content (the pricing card body). Prices come from PricingConfig;
// these control the name, popular badge, feature bullets, order and visibility.
export interface AdminPlan {
    id: string; // standard | enhanced | pro
    name: string;
    popular: boolean;
    // Priced features this plan is made of. Their sum IS the plan's price.
    featureIds: string[];
    // Legacy raw bullet text, superseded by featureIds.
    features?: string[];
    position: number;
    isActive: boolean;
    updatedAt: string;
}

// All fields optional — send only what changed. `features` is the whole list (replaces).
export type UpdatePlanRequest = Partial<Pick<AdminPlan, 'name' | 'popular' | 'featureIds' | 'position' | 'isActive'>>;

// A priced feature in the global catalog. Editing a price here re-prices every
// plan that includes the feature.
export interface AdminPlanFeature {
    id: string;
    label: string;
    price: number;
    position: number;
    isActive: boolean;
    updatedAt: string;
}

export interface CreatePlanFeatureRequest {
    // Lower-case slug. Cannot be changed later.
    id: string;
    label: string;
    price: number;
    position?: number;
    isActive?: boolean;
}

export type UpdatePlanFeatureRequest = Partial<Omit<CreatePlanFeatureRequest, 'id'>>;

// ==================== CONSENT FORM DRAFT (save & return) ====================
// Server-persisted partial of the employee consent questionnaire, keyed by the
// public link token. `formData` is the serializable answer set (no files).
export interface ConsentDraft {
    formData: Record<string, unknown>;
    currentStep: number;
    updatedAt?: string;
}

export interface SaveConsentDraftRequest {
    linkToken: string;
    formData: Record<string, unknown>;
    currentStep: number;
}

export interface SubmitEmployeeFormRequest {
    dateOfBirth: string;
    nationality: string;
    stateOfOrigin: string;
    localGovernmentArea: string;
    usa?: string;
    iin?: string;
}

// ==================== INFORMATION REQUESTS (missing info) ====================

// Widget hint for a requestable field.
//   text | textarea → string · date → ISO date string · yesno → 'yes'/'no'
//   boolean → true/false · json → repeatable array editor (sent as an array)
//   object → key/value editor for a native json column (e.g. socialMediaHandles);
//            MUST be sent as a real object, never a stringified one.
export type RequestableFieldType = 'text' | 'textarea' | 'date' | 'yesno' | 'boolean' | 'json' | 'object' | 'document';

// Current state of a `document` field, as returned in the public GET's `values[key]`.
export interface DocumentFieldValue {
    uploaded: boolean;
    fileName?: string;
    fileUrl?: string;
    uploadedAt?: string;
}

// One entry in the catalog returned by GET /investigations/requestable-fields.
export interface RequestableField {
    key: string;
    label: string;
    section: string;
    type: RequestableFieldType;
}

export type InformationRequestStatus = 'pending' | 'completed';

// A created request. When loaded publicly by token it also carries `values`.
export interface InformationRequest {
    id: string;
    investigationId: string;
    token: string;
    status: InformationRequestStatus;
    note?: string | null;
    employeeName: string;
    fields: RequestableField[];
    // Present only on the public GET-by-token response; already parsed (json → real arrays/objects).
    values?: Record<string, unknown>;
    completedAt?: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface CreateInformationRequest {
    id: string; // investigation id
    fields: string[]; // catalog keys, non-empty
    note?: string;
}

export interface SubmitInformationRequest {
    token: string;
    values: Record<string, unknown>; // scalar answers — only keys that were requested
    // Document fields keyed by the catalog field key. When present, the request is
    // sent as multipart/form-data (files + a JSON `values` field); otherwise JSON.
    documents?: Record<string, File>;
}

// ==================== RETRIGGER CONSENT ====================

// Super Admin re-issues a fresh consent link (old token dies immediately).
export interface RetriggerConsentResponse {
    id: string;
    requestLink: string; // new token
    consentLink: string;
    status: InvestigationStatus | string;
    dateRequested: string;
}

// ==================== NUDGE ====================

export type NudgeTarget = 'employee' | 'employer' | 'both';

export interface NudgeRequest {
    id: string; // investigation id
    target: NudgeTarget;
    message?: string;
}

export interface NudgeResponse {
    target: NudgeTarget;
    sent: {
        employee?: boolean;
        employer?: boolean;
    };
}

export interface AssignAgentRequest {
    agentId: string;
}

export interface SubmitReportRequest {
    reportTitle: string;
    executiveSummary?: string;
    reportDetails: string;
}

// ==================== INVESTIGATOR CHECKLIST ====================

export type ChecklistFinding = 'yes' | 'no' | 'n_a';

export interface ChecklistCatalogItem {
    id: string;
    question: string;
}

export interface ChecklistCatalogSection {
    id: string;
    title: string;
    items: ChecklistCatalogItem[];
}

// Agent-facing brief for a check tier. Drives the "Scope of Work" card and
// the marketing/pricing comparison.
export interface AgentBrief {
    checkType: CheckType;
    label: string;
    summary: string;
    scope: string[];
    workHistoryYears: number;
    estimatedDays: number;
}

export interface CheckTypeBriefsResponse {
    briefs: AgentBrief[];
}

export interface ChecklistOverviewSection {
    id: string;
    title: string;
    itemCount: number;
}

export interface ChecklistOverview {
    totalItems: number;
    sectionCount: number;
    sections: ChecklistOverviewSection[];
}

export interface InvestigationAgentBrief {
    investigationId: string;
    checkType: CheckType;
    brief: AgentBrief;
    checklistOverview: ChecklistOverview;
}

export interface ChecklistCatalogResponse {
    sections: ChecklistCatalogSection[];
    // Present when the catalog is fetched with a `checkType` filter; absent on
    // the unfiltered (admin / marketing) view.
    checkType?: CheckType | null;
    brief?: AgentBrief | null;
}

export interface ChecklistItemAnswer {
    itemId: string;
    question: string;
    finding: ChecklistFinding | null;
    notes: string | null;
    answeredById?: string | null;
    updatedAt?: string | null;
}

export interface ChecklistSection {
    id: string;
    title: string;
    items: ChecklistItemAnswer[];
}

export interface InvestigationChecklist {
    investigationId: string;
    totalItems: number;
    answeredItems: number;
    isComplete: boolean;
    sections: ChecklistSection[];
    // Backend echoes the investigation's tier + brief so the agent can see
    // exactly what scope of work they signed up for without a second roundtrip.
    checkType?: CheckType;
    brief?: AgentBrief;
}

export interface SaveChecklistItem {
    itemId: string;
    finding: ChecklistFinding;
    notes?: string;
}

export interface SaveChecklistRequest {
    items: SaveChecklistItem[];
}

export interface MissingChecklistItem {
    itemId: string;
    question: string;
    section: string;
}

export interface ChecklistIncompleteError {
    message: string;
    missingItems: MissingChecklistItem[];
}

// Read-only shape returned on the employer report (slightly looser — answeredAt instead of updatedAt).
export interface EmployerReportChecklistItem {
    itemId: string;
    question: string;
    finding: ChecklistFinding | null;
    notes?: string | null;
    answeredAt?: string | null;
}

export interface EmployerReportChecklistSection {
    id: string;
    title: string;
    items: EmployerReportChecklistItem[];
}

export interface EmployerReportChecklist {
    totalItems: number;
    answeredItems: number;
    isComplete: boolean;
    sections: EmployerReportChecklistSection[];
}

// ==================== MILESTONES ====================

export type MilestoneStatus = 'not_started' | 'in_progress' | 'submitted';

// Per-question answer inside a milestone. Mirrors ChecklistItemAnswer but scoped
// to the section the agent is currently editing.
export interface MilestoneItemAnswer {
    itemId: string;
    question: string;
    finding: ChecklistFinding | null;
    notes: string | null;
    updatedAt?: string | null;
}

// Agent-facing milestone payload. The employer/admin view drops `editable`,
// `summary`, `details`, and `items` — the server strips them server-side.
export interface InvestigationMilestone {
    order: number;
    sectionId: string;
    title: string;
    status: MilestoneStatus;
    submittedAt: string | null;
    itemCount: number;
    answeredCount: number;
    // Present on the agent view; stripped for employer/admin progress-only view.
    editable?: boolean;
    summary?: string | null;
    details?: string | null;
    items?: MilestoneItemAnswer[];
}

// One catalog section as described to the Super Admin "manage sections" UI.
export interface ManageableMilestoneSection {
    sectionId: string;
    title: string;
    // Currently part of this investigation's milestones.
    active: boolean;
    // In the check type's default scope (vs. an admin-added extra section).
    inCheckTypeScope: boolean;
    itemCount: number;
}

export interface InvestigationMilestonesResponse {
    investigationId: string;
    checkType: CheckType;
    brief?: AgentBrief | null;
    totalMilestones: number;
    submittedMilestones: number;
    allSubmitted: boolean;
    milestones: InvestigationMilestone[];
    // Only present for Super Admin / Customer Success (section-management view).
    manageableSections?: ManageableMilestoneSection[];
}

export interface MilestoneAnswerInput {
    itemId: string;
    finding: ChecklistFinding;
    notes?: string;
}

export interface SaveMilestoneDraftRequest {
    summary?: string;
    details?: string;
    answers?: MilestoneAnswerInput[];
}

export interface SubmitMilestoneRequest {
    summary: string;
    details: string;
    answers?: MilestoneAnswerInput[];
}

// 400 from POST .../submit when the section still has unanswered items.
export interface MilestoneMissingItem {
    itemId: string;
    question: string;
}

export interface MilestoneSubmissionError {
    message: string;
    missingItems: MilestoneMissingItem[];
}

// 400 from POST .../submit-report when one or more milestones are still pending.
export interface MissingMilestoneRef {
    sectionId: string;
    title: string;
}

export interface MissingMilestonesError {
    message: string;
    missingMilestones: MissingMilestoneRef[];
}

// Document-ready payload returned inside EmployerReportPayload.milestoneDocument
// once every milestone is submitted. Renders block-per-block in the employer
// report view.
export interface MilestoneDocumentFinding {
    itemId: string;
    question: string;
    finding: ChecklistFinding | null;
    notes?: string | null;
}

export interface MilestoneDocumentBlock {
    order: number;
    sectionId: string;
    title: string;
    status: MilestoneStatus;
    submittedAt: string | null;
    summary: string;
    details: string;
    findings: MilestoneDocumentFinding[];
}

export interface MilestoneDocument {
    checkType: CheckType;
    totalBlocks: number;
    submittedBlocks: number;
    isComplete: boolean;
    blocks: MilestoneDocumentBlock[];
}

// ==================== ADDRESS VISITS ====================

export type AddressVisitType =
    | 'home'
    | 'work'
    | 'reference'
    | 'guarantor' // legacy single-guarantor value (kept so old records still render)
    | 'guarantor_1'
    | 'guarantor_2'
    | 'prior_employer'
    | 'education'
    | 'other';

export interface InvestigationAddressVisit {
    id: string;
    investigationId: string;
    agentId: string;
    visitType: AddressVisitType;
    observedAddress: string;
    // MySQL DECIMAL columns serialize as strings via TypeORM; tolerate both shapes.
    latitude: number | string;
    longitude: number | string;
    accuracyMeters?: number | null;
    visitedAt?: string | null;
    notes?: string | null;
    photos?: string[] | null;
    createdAt: string;
    updatedAt: string;
}

export interface CreateAddressVisitRequest {
    visitType: AddressVisitType;
    observedAddress: string;
    latitude: number;
    longitude: number;
    accuracyMeters?: number;
    visitedAt?: string;
    notes?: string;
    photos?: string[];
}

export interface EmployerReportAddressVisit {
    id: string;
    visitType: AddressVisitType | string;
    observedAddress: string;
    location: {
        latitude: number | string;
        longitude: number | string;
        accuracyMeters?: number | null;
        mapsUrl: string;
    };
    visitedAt?: string | null;
    notes?: string | null;
    photos?: string[] | null;
    agentId?: string;
    loggedAt?: string;
}

// ==================== EMPLOYER REPORT (GET /investigations/:id/employer-report) ====================

export type EmployerReportColor = 'green' | 'orange' | 'red';

export interface EmployerReportEmployee {
    firstName: string;
    middleName?: string | null;
    lastName: string;
    fullName: string;
    email?: string;
    phone?: string;
    gender?: string;
    passportPhotoUrl?: string | null;
    // Passport inlined as a base64 data URL by the backend — same-origin from the
    // browser's view, so it always rasterizes into the exported PDF (no CORS).
    passportPhotoDataUrl?: string | null;
}

export interface EmployerReportHeader {
    investigationId: string;
    supportCode?: string | null;
    reportPin?: string | null;
    status: InvestigationStatus | string;
    isReportValidated: boolean | null;
    validatedAt: string | null;
    validationNotes: string | null;
    employee: EmployerReportEmployee;
}

export interface EmployerReportRequest {
    checkType: string;
    checkTypeDisplay: string;
    amountPaid: number;
    serviceCharge: number | null;
    currency: string;
    isPaid: boolean;
    dateRequested: string;
    dateConsentGiven: string | null;
    dateFormSubmitted: string | null;
    reportSubmittedDate: string | null;
}

export interface EmployerReportAgent {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
}

export interface EmployerReportFieldReport {
    title: string;
    executiveSummary: string;
    details: string;
    submittedAt: string | null;
    agent: EmployerReportAgent | null;
}

export interface EmployerReportRiskGauge {
    riskPercentage: number;
    verificationPercentage: number;
    riskLevel: string;
    riskLevelLabel: string;
    color: EmployerReportColor;
}

export interface EmployerReportVerificationRow {
    category: string;
    label: string;
    // Not applicable for this tier / no substantive findings → renders as N/A.
    applicable?: boolean;
    verificationPercentage: number | null;
    color: EmployerReportColor | null;
    rawScore: number | null;
    weight: number | null;
    rationale?: string | null;
}

export interface EmployerReportDocument {
    id: string;
    fileName: string;
    fileUrl: string;
    mimeType: string;
    fileSize: number;
    uploadedAt: string;
}

export interface EmployerReportSupportingDocument extends EmployerReportDocument {
    documentType: string;
    uploadedBy: string;
}

export interface EmployerReportBranding {
    productName: string;
    intro: string;
    disclaimer: string;
}

export interface EmployerReportPayload {
    header: EmployerReportHeader;
    request: EmployerReportRequest;
    fieldReport: EmployerReportFieldReport;
    riskGauge: EmployerReportRiskGauge;
    verificationBreakdown: EmployerReportVerificationRow[];
    addressVisits?: EmployerReportAddressVisit[];
    checklist?: EmployerReportChecklist;
    milestoneDocument?: MilestoneDocument;
    riskAssessment?: unknown;
    personalIdentification?: unknown;
    criminalDisclosure?: unknown;
    financialAndDirectorship?: unknown;
    safeguardingConduct?: unknown;
    litigation?: unknown;
    reputational?: unknown;
    conflictOfInterest?: unknown;
    consents?: Record<string, boolean>;
    documents?: {
        passportPhoto?: EmployerReportDocument | null;
        governmentId?: EmployerReportDocument | null;
        signedAffidavit?: EmployerReportDocument | null;
        supportingDocuments?: EmployerReportSupportingDocument[];
    };
    employer?: {
        id: string;
        firstName: string;
        lastName: string;
        email?: string;
        companyName?: string;
    } | null;
    timestamps?: { createdAt: string; updatedAt: string };
    branding: EmployerReportBranding;
}

export interface InvestigationStats {
    total: number;
    new: number;
    pending: number;
    in_progress: number;
    completed: number;
    cancelled: number;
    averageCompletionDays?: number;
    completionRate?: number;
}

// ==================== CHECK OPTIONS / PRICING TYPES ====================

export interface CheckOption {
    id: string;
    name: string;
    // `price` is an alias for `employerPrice` (kept for backward compat with older callers).
    price: number;
    currency: string;
    popular: boolean;
    features: string[];
    // Referral-aware pricing fields (present on authenticated employer calls).
    listPrice?: number;
    employerPrice?: number;
    discountAmount?: number;
    discountRate?: number;
    referralApplied?: boolean;
    // Medical Analysis add-on availability + pricing for this plan.
    // Superseded by `addons` (medical is one of them) but still sent, so older
    // callers keep working.
    medicalAnalysis?: {
        available: boolean;
        addonPrice: number;
        priceWithMedicalAnalysis: number;
        // Pre-formatted "Medical Analysis (PCV, HBV, …)" — the union of all tests.
        label?: string;
        // Exact per-category panels when the booking category is known.
        tests?: Partial<Record<InvestigationCategory | string, string[]>>;
    };
    // The plan's own features, each with a standalone price. Taking every one
    // earns the whole-plan discount; picking a few is charged at list price.
    featureDetails?: PlanFeatureOption[];
    featureIds?: string[];
    // What the same features cost bought one by one, and what taking the whole
    // plan saves against that.
    separatePrice?: number;
    wholePlanDiscountPercent?: number;
    wholePlanSaving?: number;
    wholePlanPrice?: number;
    // Extras sold on top of the plan, at face value. The markup never applies
    // to these.
    addonsAvailable?: boolean;
    addons?: CheckAddonOption[];
}

// One priced line inside a plan. `price` is the standalone price: what this
// check costs bought on its own.
export interface PlanFeatureOption {
    id: string;
    label: string;
    price: number;
}

// One add-on on a plan card. `price` is the add-on alone; `priceWithAddon` is
// the plan's employer price with just this one added.
export interface CheckAddonOption {
    id: string;
    name: string;
    description: string;
    price: number;
    priceWithAddon: number;
}

// POST /payments/quote — price a live selection exactly as checkout will.
export interface SelectionQuoteRequest {
    checkType: CheckType | string;
    // Omit to price the whole plan, which is the normal case.
    features?: string[];
    addons?: string[];
}

export interface SelectionQuoteResponse {
    checkType: string;
    currency: string;
    // Echoes what was actually counted; ids not on this plan are dropped.
    featureIds: string[];
    features: PlanFeatureOption[];
    featuresSubtotal: number;
    // True means every feature was taken, so the whole-plan discount applied.
    isWholePlan: boolean;
    wholePlanDiscountPercent: number;
    wholePlanDiscountAmount: number;
    featuresTotal: number;
    // What the whole plan would cost, for the comparison on the card.
    wholePlanPrice: number;
    addonIds: string[];
    addons: { id: string; name: string; description: string; price: number }[];
    addonsTotal: number;
    // What checkout will charge, before any promo code.
    total: number;
}

// ==================== CUSTOMER SUCCESS TYPES ====================

export interface AssignMedicalResponse {
    medicalUserId: string;
    status: string;
}

// CS progress checklist — section-level boolean tracker (separate from the agent's
// yes/no/n-a findings checklist), driven by the investigation's checkType. The
// checkbox lives on the section; items are display-only context.
export interface CsChecklistItem {
    id: string;
    question: string;
}

export interface CsChecklistSection {
    id: string;
    title: string;
    checked: boolean;
    checkedAt?: string | null;
    checkedById?: string | null;
    items: CsChecklistItem[];
}

export interface CsChecklistProgress {
    checked: number; // sections done
    total: number; // total sections
    percent: number;
}

export interface CsChecklist {
    investigationId: string;
    checkType: CheckType | string;
    progress: CsChecklistProgress;
    sections: CsChecklistSection[];
}

export interface UpdateCsChecklistRequest {
    id: string; // investigation id
    sections: { sectionId: string; checked: boolean }[];
}

// Shared shape for notify-employee / update-employer responses.
export interface MessageSentResponse {
    sent: boolean;
    channels: string[];
}

export interface NotifyMessageRequest {
    message: string;
    subject?: string;
}

// Pay summary reused by the CS + Medical dashboard metrics.
export interface MetricsEarnings {
    total: number;
    paid: number;
    pending: number;
    byCheckType: { checkType: string; total: number; count: number }[];
}

// GET /customer-success/metrics
export interface CsMetrics {
    investigations: {
        total: number;
        byStatus: Partial<Record<InvestigationStatus | string, number>>;
        awaitingAgentAssignment: number;
        awaitingMedicalAssignment: number;
        guarantorTownWarnings: number;
    };
    earnings: MetricsEarnings;
}

// GET /medical/metrics
export interface MedicalMetrics {
    investigations: {
        total: number;
        reportSubmitted: number;
        reportPending: number;
        byCategory: Partial<Record<InvestigationCategory | string, number>>;
    };
    earnings: MetricsEarnings;
}

// ==================== MEDICAL TYPES ====================

// One required test in a category panel.
export interface MedicalTest {
    key: string;
    label: string;
}

export interface MedicalTestPanel {
    category: InvestigationCategory | string;
    tests: MedicalTest[];
}

// A single test result inside the report. `result` is required on submit; the
// rest are optional annotations.
export interface MedicalTestResult {
    result: string;
    status?: string;
    notes?: string;
}

export interface InvestigationMedicalReport {
    id: string;
    investigationId: string;
    results: Record<string, MedicalTestResult>;
    summary?: string | null;
    facilityName?: string | null;
    testedAt?: string | null;
    status: MedicalReportStatus | string;
    enteredById?: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface SubmitMedicalReportRequest {
    results: Record<string, MedicalTestResult>;
    summary?: string;
    facilityName?: string;
    testedAt?: string;
}

// ==================== MEDICAL LABS ====================

export interface MedicalLab {
    id: string;
    name: string;
    brand?: string | null;
    partnerName?: string | null;
    area?: string | null;
    address: string;
    city?: string | null;
    state?: string | null;
    region?: string | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface MedicalLabFilters {
    state?: string;
    region?: string;
    search?: string;
    includeInactive?: boolean;
}

// data from POST .../assign-lab
export interface AssignLabResponse {
    status: string;
    labId: string;
    labName: string;
    emailedTo: string;
    emailSent: boolean;
}

export interface CreateMedicalLabRequest {
    name: string;
    address: string;
    brand?: string;
    partnerName?: string | null;
    area?: string;
    city?: string;
    state?: string;
    region?: string;
    isActive?: boolean;
}

export type UpdateMedicalLabRequest = Partial<CreateMedicalLabRequest>;

// ==================== RISK ASSESSMENT TYPES ====================

export interface ScoringCriteriaOption {
    score: number;
    label: string;
}

export type ScoringCriteriaResponse = Record<string, ScoringCriteriaOption[]>;

export interface RiskCategoryScore {
    id?: string;
    category: string;
    rawScore: number;
    selectedCriteria: string;
    rationale?: string;
    evidenceReferences?: string;
    weight?: number;
    weightedScore?: number;
    scoredById?: string;
    createdAt?: string;
    updatedAt?: string;
    version?: number;
}

export interface RiskAssessmentSubmission {
    categoryScores: Pick<
        RiskCategoryScore,
        'category' | 'rawScore' | 'selectedCriteria' | 'rationale' | 'evidenceReferences'
    >[];
    investigationNotes: string;
}

export interface RiskAssessmentResponse {
    totalScore: number;
    maxScore: number;
    percentage: number;
    riskLevel: RiskLevel;
    recommendation: RiskRecommendation;
    autoRejectReason?: string | null;
    version?: number;
    categoryScores: RiskCategoryScore[];
}

export interface RiskAssessmentHistoryResponse {
    success: boolean;
    data: RiskAssessmentResponse[];
}

export interface DocumentApprovalRequest {
    status: 'approved' | 'rejected' | 'completed';
    notes?: string;
}

// ==================== DOCUMENT TYPES ====================

export interface InvestigationDocument {
    id: string;
    investigationId: string;
    documentType: DocumentType;
    fileName: string;
    fileUrl: string;
    downloadUrl?: string;
    fileSize: number;
    mimeType: string;
    uploadedBy?: DocumentUploader;
    approvalStatus?: DocumentApprovalStatus;
    approvedById?: string;
    approvalNotes?: string;
    approvedAt?: string;
    uploadedAt: string;
    updatedAt?: string;
}

export interface UploadDocumentResponse {
    id: string;
    investigationId: string;
    documentType: DocumentType;
    fileName: string;
    fileUrl: string;
    fileSize: number;
    mimeType: string;
    uploadedAt: string;
}

export interface DocumentValidationResponse {
    isValid: boolean;
    missingDocuments: string[];
    uploadedDocuments: {
        type: DocumentType;
        uploaded: boolean;
        fileName?: string;
    }[];
}

// ==================== PAYMENT TYPES ====================

// Gateway-agnostic payment types. The active gateway is Monnify; the API
// contract (request/response shape) is identical regardless of provider.
// One person in a bulk purchase. The plan, features and add-ons are chosen once
// for the whole batch, so a subject carries only who they are.
export interface BatchSubject {
    employeeFirstName: string;
    employeeMiddleName?: string;
    employeeLastName: string;
    employeeEmail: string;
    employeePhone: string;
    employeeGender?: string;
    investigationCategory?: InvestigationCategory;
}

export interface CreateInvestigationBatchRequest {
    checkType: CheckType;
    features?: string[];
    addons?: string[];
    investigationCategory?: InvestigationCategory;
    subjects: BatchSubject[];
    transactionId?: string;
}

export interface CreateInvestigationBatchResponse {
    batchId: string;
    subjectCount: number;
    pricePerSubject: number;
    total: number;
    isComplimentary: boolean;
    isPaid: boolean;
    investigations: {
        id: string;
        employeeFirstName: string;
        employeeLastName: string;
        employeeEmail: string;
    }[];
}

export interface InitiatePaymentRequest {
    checkType: CheckType;
    redirectUrl?: string;
    investigationId?: string;
    // Set INSTEAD of investigationId for a bulk purchase: one charge covering
    // every investigation in the batch. Sending both is rejected.
    batchId?: string;
    // Legacy single-add-on flag. Superseded by `addons`; the backend merges the
    // two and never double-charges, so either (or both) is safe to send.
    medicalAnalysisIncluded?: boolean;
    // Extras bought on top of the plan, at face value.
    addons?: string[];
    // Plan features being bought. Omit for the whole plan (the normal case);
    // a subset is charged at its sum plus the cherry-pick markup.
    features?: string[];
    // Optional one-time promo code (percentage or free).
    promoCode?: string;
}

export interface InitiatePaymentResponse {
    paymentLink: string;
    txRef: string;
    transactionId: string;
    amount: number;
    currency?: string;
    // 'monnify' for the current gateway (was 'flutterwave').
    provider?: string;
    // Promo fields — present when a promoCode was applied.
    promoApplied?: boolean;
    discountAmount?: number;
    // Free-code path: no gateway charge. `paid` distinguishes investigation-first
    // (true → already paid) from pure payment-first (false → create with txn id).
    free?: boolean;
    paid?: boolean;
}

export interface VerifyPaymentRequest {
    tx_ref: string;
}

export interface VerifyPaymentResponse {
    success: boolean;
    transactionId: string;
    status: 'successful' | 'failed' | 'cancelled' | string;
    amount: number;
    checkType: CheckType;
}

export interface Transaction {
    id: string;
    transactionId: string;
    investigationId: string | null; // Can be null if payment made but not yet linked to investigation
    checkType: CheckType; // Added to support payment-first flow
    amount: number;
    currency: string;
    status: PaymentStatus;
    paymentMethod?: string;
    datePaid?: string; // Added to match API response
    createdAt: string;
    updatedAt: string;
    investigation?: {
        id: string;
        employeeFirstName: string;
        employeeLastName: string;
    };
}

// ==================== PROMO CODES ====================

export type PromoRewardType = 'percentage' | 'free';
export type PromoStatus = 'active' | 'used' | 'disabled';

export interface PromoCode {
    id: string;
    code: string;
    rewardType: PromoRewardType;
    percentOff?: number | null;
    status: PromoStatus;
    note?: string | null;
    expiresAt?: string | null;
    usedByUserId?: string | null;
    usedByEmail?: string | null;
    usedAt?: string | null;
    transactionId?: string | null;
    investigationId?: string | null;
    createdById?: string | null;
    /** `null` = unlimited. Codes created before multi-use shipped read as 1. */
    maxRedemptions?: number | null;
    /** Consumed so far; the API never lets this exceed the cap. */
    redemptionCount?: number;
    /** Stops one person draining a 50-use code. Defaults to true server-side. */
    oncePerUser?: boolean;
    /** Server-computed convenience fields on list/detail — `null` when unlimited. */
    remainingRedemptions?: number | null;
    isUnlimited?: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface CreatePromoCodeRequest {
    rewardType: PromoRewardType;
    percentOff?: number; // required when rewardType === 'percentage'
    code?: string; // omit to auto-generate
    note?: string;
    expiresAt?: string;
    /**
     * `null` = unlimited, N = capped. Omitting the field entirely means single
     * use, which is the pre-existing behaviour — so it must stay omitted rather
     * than sent as 1, to keep old and new create calls identical on the wire.
     */
    maxRedemptions?: number | null;
    oncePerUser?: boolean;
}

/**
 * PATCH /admin/promo-codes/:id — every field optional, send only what changes.
 *
 * Raising `maxRedemptions` on an exhausted code reactivates it implicitly, so
 * `reactivate` is only needed for the other cases (e.g. extending the expiry of
 * a disabled code). Lowering the cap below `redemptionCount` is rejected by the
 * API rather than silently truncating.
 */
export interface UpdatePromoCodeRequest {
    maxRedemptions?: number | null;
    oncePerUser?: boolean;
    expiresAt?: string | null;
    reactivate?: boolean;
}

/** One row of GET /admin/promo-codes/:id/redemptions. */
export interface PromoRedemption {
    id: string;
    userId?: string | null;
    userEmail?: string | null;
    redeemedAt?: string | null;
    transactionId?: string | null;
    investigationId?: string | null;
    discountAmount?: number | null;
}

export interface PromoQuoteRequest {
    code: string;
    checkType: CheckType | string;
    medicalAnalysisIncluded?: boolean;
    // Add-ons to include in the base amount — the promo applies after the
    // bundle discount.
    addons?: string[];
}

export interface PromoQuoteResponse {
    code: string;
    rewardType: PromoRewardType;
    percentOff?: number | null;
    isFree: boolean;
    baseAmount: number;
    discountAmount: number;
    finalAmount: number;
}

// ==================== MESSAGING (SMS) ====================

export interface SendSmsRequest {
    phoneNumber: string;
    message: string;
}

export interface SendSmsResponse {
    success: boolean;
    phoneNumber: string;
}

// ==================== ADMIN TRANSACTIONS ====================

export interface AdminTransaction {
    id: string;
    transactionId: string;
    amount: number;
    status: string;
    paymentMethod?: string;
    gateway?: string;
    checkType?: string;
    promoCode?: string | null;
    employeeName?: string | null;
    datePaid?: string | null;
    createdAt: string;
    user?: User | null;
    investigation?: Investigation | null;
}

export interface AdminTransactionFilters {
    status?: string;
    paymentMethod?: string;
    userId?: string;
    investigationId?: string;
    checkType?: string;
    promoCode?: string;
    search?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
}

export interface AdminTransactionsResponse {
    data: AdminTransaction[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
    summary: {
        totalTransactions: number;
        successfulRevenue: number;
        byStatus: Record<string, { count: number; amount: number }>;
    };
}

export interface ReusablePayment {
    transactionId: string;
    checkType: CheckType;
    amount: number;
    status: PaymentStatus;
    datePaid: string;
    investigationId: string | null;
}

// ==================== EARNINGS TYPES ====================

export interface Earning {
    id: string;
    agentId: string;
    investigationId: string;
    amount: number;
    amountEarned?: string;
    employeeName?: string;
    checkType?: CheckType | string;
    status: EarningStatus;
    dateSubmitted?: string;
    datePaid?: string;
    createdAt: string;
    updatedAt?: string;
    paidAt?: string;
    agent?: User;
    investigation?: Investigation;
}

export interface EarningsSummary {
    totalEarnings: number;
    paidEarnings: number;
    pendingEarnings: number;
    totalCount: number;
    paidCount: number;
    pendingCount: number;
}

export interface EarningsBreakdown {
    total: number;
    paid: number;
    pending: number;
    byCheckType: {
        checkType: string;
        total: number;
        count: number;
    }[];
}

export interface PlatformEarningsStats {
    totalEarnings: number;
    paidEarnings: number;
    pendingEarnings: number;
    totalCount: number;
    paidCount: number;
    pendingCount: number;
    averageEarningPerInvestigation: number;
}

export interface MarkEarningPaidResponse {
    id: string;
    status: EarningStatus;
    paidAt: string;
    message: string;
}

export interface BulkMarkPaidRequest {
    earningIds: string[];
}

export interface BulkMarkPaidResponse {
    updatedCount: number;
    message: string;
}

// ==================== ANALYTICS TYPES ====================

export interface DashboardStats {
    investigations: {
        total: number;
        ongoing: number;
        completed: number;
        pending: number;
        new: number;
    };
    financial: {
        totalRevenue?: number;
        totalEarnings?: number;
        pendingPayments?: number;
        totalSpent?: number;
        platformProfit?: number;
    };
    users?: {
        totalEmployers: number;
        totalAgents: number;
        activeUsers: number;
    };
}

export interface InvestigationTrend {
    date: string;
    count: number;
    status: string;
}

export interface RevenueTrend {
    date: string;
    revenue: number;
    count: number;
}

export interface TopAgent {
    agentId: string;
    firstName: string;
    lastName: string;
    completedInvestigations: number;
    avgCompletionDays: number;
}

export interface StatusDistribution {
    status: string;
    count: number;
}

export interface FinancialStats {
    totalRevenue?: number;
    totalSpent?: number;
    totalEarnings?: number;
    paidEarnings?: number;
    pendingEarnings?: number;
    platformProfit?: number;
    averageTransactionAmount?: number;
    averageEarningPerInvestigation?: number;
    transactionCount?: number;
}

// ==================== ADMIN TYPES ====================

export interface InviteAgentRequest {
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
}

// Roles an admin can create via POST /admin/users. `employer` self-registers and
// `super_admin` can't be created here.
export type StaffRole =
    | UserRole.AGENT
    | UserRole.CUSTOMER_SUCCESS
    | UserRole.MEDICAL
    | UserRole.HR
    | UserRole.NOTARY
    | UserRole.POLICE;

export interface CreateStaffRequest {
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    role: StaffRole;
    password?: string;
}

// ==================== NOTARY ====================

// A stored affidavit file reference (employee-signed or notary's final copy).
export interface AffidavitFile {
    fileName: string;
    fileUrl: string;
    uploadedAt: string;
}

// Row in the notary's assignment list (GET /notary/investigations).
export interface NotaryInvestigationListItem {
    id: string;
    employeeName: string;
    employerName?: string | null;
    status: InvestigationStatus | string;
    isFormSubmitted: boolean;
    notaryCompletedAt?: string | null;
}

// Privacy-scoped detail for the notary — basic info + affidavits only, never the
// questionnaire/disclosures (GET /notary/investigations/:id).
export interface NotaryInvestigationDetail {
    id: string;
    status: InvestigationStatus | string;
    isFormSubmitted: boolean;
    employee: {
        firstName?: string;
        middleName?: string;
        lastName?: string;
        email?: string;
        phone?: string;
        gender?: string;
    };
    employer: {
        companyName?: string;
        contactName?: string;
        email?: string;
        phone?: string;
    };
    signedAffidavit: AffidavitFile | null;
    notarizedAffidavit: AffidavitFile | null;
    notaryCompletedAt?: string | null;
}

// ==================== POLICE WORKSPACE ====================

export interface PoliceInvestigationListItem {
    id: string;
    employeeName: string;
    status: InvestigationStatus | string;
    isFormSubmitted: boolean;
    policeCompletedAt?: string | null;
}

// A downloadable file returned on the police detail view.
export interface PoliceFile {
    fileName: string;
    fileUrl: string;
    uploadedAt?: string | null;
}

// Privacy-scoped detail for the police user — basic profile + photo + certificate
// only (GET /police/investigations/:id).
export interface PoliceInvestigationDetail {
    id: string;
    status: InvestigationStatus | string;
    isFormSubmitted: boolean;
    employee: {
        name?: string;
        address?: string;
        email?: string;
        telephone?: string;
        sex?: string;
        dateOfBirth?: string;
        placeOfBirth?: string;
        stateOfOrigin?: string;
        nationality?: string;
        nin?: string;
    };
    photograph: PoliceFile | null;
    characterCertificate: PoliceFile | null;
    policeCompletedAt?: string | null;
}

// Admin-editable pricing config (single DB-backed row). All amounts in NGN (kobo-free integers).
export interface PricingConfig {
    id: string;
    standard: number;
    enhanced: number;
    pro: number;
    // Alias kept for back-compat — forwarded to the `medical_analysis` add-on
    // in the catalog. Prefer editing the add-on itself.
    medicalAnalysisAddon: number;
    // Percent off when a customer takes every feature in a plan (0–100).
    // Features carry standalone prices; the plan is their sum less this.
    wholePlanDiscountPercent: number;
    // Plan prices derived from the feature catalog. Read only: change a plan's
    // price by re-pricing its features, not by typing a number here.
    planPrices?: Record<string, number>;
    agentFlatEarning: number;
    reassignedGuarantorFee: number;
    customerSuccessEarning: number;
    medicalEarning: number;
    updatedAt: string;
}

// Any subset of the editable numeric fields.
export type UpdatePricingRequest = Partial<
    Pick<
        PricingConfig,
        | 'standard'
        | 'enhanced'
        | 'pro'
        | 'medicalAnalysisAddon'
        | 'wholePlanDiscountPercent'
        | 'agentFlatEarning'
        | 'reassignedGuarantorFee'
        | 'customerSuccessEarning'
        | 'medicalEarning'
    >
>;

// ==================== ADMIN CHECK ADD-ON CATALOG ====================

// A super-admin managed add-on. `id` is the stable checkout key and is fixed at
// creation; everything else is editable at runtime.
export interface AdminCheckAddon {
    id: string;
    name: string;
    description: string;
    price: number;
    // Check types this add-on is sold on. null / [] means every tier — use it
    // to keep an add-on off a tier that already includes the same work.
    availableFor: string[] | null;
    position: number;
    // False withdraws it from checkout without touching past investigations.
    isActive: boolean;
    updatedAt: string;
}

export interface CreateCheckAddonRequest {
    // Lower-case slug (letters, digits, underscores). Cannot be changed later.
    id: string;
    name: string;
    description?: string;
    price: number;
    availableFor?: string[] | null;
    position?: number;
    isActive?: boolean;
}

export type UpdateCheckAddonRequest = Partial<Omit<CreateCheckAddonRequest, 'id'>>;

// Filters for the admin all-investigations oversight query.
export interface AdminInvestigationFilters {
    status?: string;
    agentId?: string;
    employerId?: string;
    customerSuccessId?: string;
    medicalUserId?: string;
    notaryUserId?: string;
    policeUserId?: string;
    category?: InvestigationCategory | string;
}

export interface UpdateUserStatusRequest {
    status: UserStatus;
}

export interface ReassignInvestigationRequest {
    agentId: string;
}

export interface PlatformStatistics {
    users: {
        total: number;
        employers: number;
        agents: number;
        active: number;
    };
    investigations: {
        total: number;
        completed: number;
        completionRate: string;
    };
    financial: {
        totalRevenue: number;
        totalEarnings: number;
        platformProfit: number;
    };
}

// ==================== WAITLIST TYPES ====================

export interface WaitlistEntry {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    notes?: string;
}

// ==================== CALLBACK REQUESTS ====================

export interface CallbackRequestPayload {
    name: string;
    email: string;
    phoneNumber: string;
    /** Which surface produced the lead, so marketing can attribute it. */
    source?: string;
    /** Plan the visitor was looking at when they asked to be called, if any. */
    planId?: string;
}

export interface CallbackRequestResponse {
    id?: string;
    success: boolean;
    message?: string;
}

export type CallbackStatus = 'new' | 'contacted' | 'converted' | 'dropped';
export type CallbackPlanId = 'standard' | 'enhanced' | 'pro';

/**
 * A lead captured by the public callback form.
 *
 * Marketing PII — `name`, `email` and `phoneNumber` must never reach analytics.
 * The public form only ever reports `source` and `planId`; the admin queue holds
 * that same boundary.
 *
 * Note the API also returns `emailKey` (a lowercased copy of `email` used for
 * server-side dedupe). It is deliberately absent here — rendering it just looks
 * like a duplicate column.
 */
export interface Callback {
    id: string;
    name: string;
    email: string;
    /** Always canonical `+234…`, so it is safe to build `tel:`/`wa.me` links from. */
    phoneNumber: string;
    source: string | null;
    planId: CallbackPlanId | null;
    status: CallbackStatus;
    /** Abuse-triage only — keep out of the default table. */
    ipAddress: string | null;
    userAgent: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface Paginated<T> {
    items: T[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

/**
 * The complete query surface. The API runs `forbidNonWhitelisted`, so any extra
 * param (`search`, `sort`, `from`, a stale `q`) is a 400 rather than being
 * ignored — do not widen this without a backend change.
 */
export interface CallbackListParams {
    status?: CallbackStatus;
    page?: number;
    limit?: number;
}

export interface WaitlistResponse {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    status: WaitlistStatus;
    notes?: string;
    createdAt: string;
    updatedAt: string;
}

export interface WaitlistStats {
    total: number;
    pending: number;
    contacted: number;
    converted: number;
}

export interface CheckEmailResponse {
    exists: boolean;
    status?: WaitlistStatus;
}

export interface UpdateWaitlistRequest {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    status?: WaitlistStatus;
    notes?: string;
}

export interface BulkUpdateWaitlistRequest {
    ids: number[];
    status: WaitlistStatus;
}

// ==================== COMMON API RESPONSE TYPES ====================

export interface ApiResponse<T = unknown> {
    data?: T;
    message?: string;
    success?: boolean;
    errors?: string[];
}

export interface ApiError {
    message: string;
    status: number;
    response?: unknown;
}

export interface PaginatedResponse<T> {
    data: T[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

// ==================== FORM TYPES ====================

export interface FormField {
    name: string;
    label: string;
    type: 'text' | 'email' | 'password' | 'number' | 'textarea' | 'select';
    required?: boolean;
    placeholder?: string;
    options?: { value: string; label: string }[];
}

// ==================== NAVIGATION TYPES ====================

export interface NavItem {
    id: string;
    label: string;
    path: string;
    icon?: React.ComponentType<{ className?: string }>;
    roles?: UserRole[];
    children?: NavItem[];
}

// ==================== SUPPORT TICKET TYPES ====================

export interface SupportMessage {
    id: string;
    ticketId?: string;
    senderType: 'employee' | 'admin' | 'employer';
    senderName?: string | null;
    senderUserId?: string | null;
    body: string;
    createdAt: string;
}

export interface SupportTicket {
    id: string;
    userId?: string | null;
    // Employee ticket fields (no auth, identified by supportCode)
    investigationId?: string | null;
    supportCode?: string | null;
    employeeName?: string | null;
    employeeEmail?: string | null;
    subject: string;
    message: string;
    category: TicketCategory;
    status: TicketStatus;
    priority: TicketPriority;
    assignedToId?: string;
    adminResponse?: string;
    lastMessageAt?: string | null;
    messages?: SupportMessage[];
    resolvedAt?: string;
    resolvedById?: string;
    createdAt: string;
    updatedAt: string;
    user?: User;
    investigation?: Investigation;
    assignedTo?: User;
    resolvedBy?: User;
}

export interface CreateTicketRequest {
    subject: string;
    message: string;
    category?: TicketCategory;
}

// ==================== EMPLOYEE SUPPORT (supportCode-based) ====================

export interface EmployeeOpenTicketRequest {
    supportCode: string;
    subject: string;
    message: string;
    category?: TicketCategory | string;
}

export interface EmployeeReplyRequest {
    supportCode: string;
    body: string;
}

export interface AdminReplyRequest {
    body: string;
}

// ==================== EMPLOYER SUPPORT (auth, role=EMPLOYER, ownership-scoped) ====================

export interface OpenEmployerTicketRequest {
    investigationId: string;
    subject: string;
    message: string;
    category?: TicketCategory | string;
}

export interface EmployerReplyRequest {
    body: string;
}

// ==================== SUPPORT BLOCK ON SUBMIT-FORM ====================

export interface InvestigationSupportBlock {
    code: string;
    link: string;
    email: string;
    message: string;
}

export interface SubmitFormResponse extends Investigation {
    supportCode?: string;
    support?: InvestigationSupportBlock;
}

export interface UpdateTicketStatusRequest {
    status: TicketStatus;
    adminResponse?: string;
}

export interface UpdateTicketPriorityRequest {
    priority: TicketPriority;
}

export interface AssignTicketRequest {
    adminId: string;
}

export interface TicketStatistics {
    totalTickets: number;
    openTickets: number;
    inProgressTickets: number;
    resolvedTickets: number;
    closedTickets: number;
    urgentTickets: number;
    activeTickets: number;
}

// ==================== BANK ACCOUNT TYPES ====================

export interface BankAccount {
    id: string;
    bankName: string;
    accountNumber: string;
    accountName: string;
    bankCode?: string;
    isDefault: boolean;
    createdAt: string;
    updatedAt: string;
    user?: User;
}

export interface AddBankAccountRequest {
    bankName: string;
    accountNumber: string;
    accountName: string;
    bankCode?: string;
    isDefault?: boolean;
}

export interface UpdateBankAccountRequest {
    bankName?: string;
    accountNumber?: string;
    accountName?: string;
    bankCode?: string;
    isDefault?: boolean;
}

// ==================== WITHDRAWAL TYPES ====================

export enum WithdrawalStatus {
    PENDING = 'pending',
    APPROVED = 'approved',
    REJECTED = 'rejected',
    PROCESSING = 'processing',
    COMPLETED = 'completed',
    FAILED = 'failed',
}

export interface Withdrawal {
    id: string;
    amount: number;
    status: WithdrawalStatus;
    reason?: string;
    reviewNotes?: string;
    transactionReference?: string;
    reviewedBy?: string;
    reviewedAt?: string;
    processedAt?: string;
    bankAccount: {
        id: string;
        bankName: string;
        accountNumber: string;
        accountName: string;
    };
    user?: User;
    createdAt: string;
    updatedAt: string;
}

export interface CreateWithdrawalRequest {
    bankAccountId: string;
    amount: number;
    reason?: string;
}

export interface ReviewWithdrawalRequest {
    status: 'approved' | 'rejected';
    reviewNotes?: string;
}

export interface ProcessWithdrawalRequest {
    status: 'completed' | 'failed';
    transactionReference: string;
}

export interface WithdrawalStatistics {
    totalRequests: number;
    pendingRequests: number;
    approvedRequests: number;
    completedRequests: number;
    rejectedRequests: number;
    totalAmountRequested: number;
    totalAmountCompleted: number;
    totalAmountPending: number;
}

// ==================== HR REFERRAL PROGRAM ====================

export type HrReferralPipelineStage = 'invited' | 'signed_up' | 'first_investigation' | 'active';

export type HrEarningStatus = 'pending' | 'paid' | 'voided';

export interface HrMeResponse {
    referralCode: string;
    shareUrl: string;
}

export interface HrReferral {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    companyName?: string | null;
    signedUpAt: string;
    referralActive: boolean;
    investigationCount: number;
    totalCommission: number;
    paidCommission: number;
    pipelineStage: HrReferralPipelineStage;
}

export interface HrReferralEmployer {
    id: string;
    companyName?: string | null;
    firstName: string;
    lastName: string;
}

export interface HrEarning {
    id: string;
    hrId: string;
    employerId: string;
    investigationId: string;
    listPrice: number;
    commissionRate: number;
    amount: number;
    checkType: string;
    employeeName: string;
    status: HrEarningStatus;
    paidAt: string | null;
    voidedAt: string | null;
    voidReason: string | null;
    createdAt: string;
    employer?: HrReferralEmployer;
    investigation?: Investigation;
}

export interface HrEarningsSummary {
    pending: number;
    paid: number;
    voided: number;
    lifetimeGross: number;
    counts: { pending: number; paid: number; voided: number };
    minPayoutAmount: number;
}

export interface HrLeaderboardEntry {
    rank: number;
    hrId: string;
    hrName: string;
    activeEmployers: number;
    monthCommission: number;
}

// ==================== TRAINING / CERTIFICATION TYPES ====================

export type EnrollmentStatus = 'in_progress' | 'certified';
export type VideoSourceType = 'link' | 'upload';

export interface TrainingCourseSummary {
    id: string;
    title: string;
    description?: string;
    passingScore: number;
    totalVideos: number;
    passedVideos: number;
    status: EnrollmentStatus | null; // null if never started
    certified: boolean;
}

export interface TrainingVideo {
    id: string;
    title: string;
    description?: string;
    sourceType: VideoSourceType;
    orderIndex: number;
    durationSeconds: number;
    questionCount: number;
    watched: boolean;
    passed: boolean;
    bestScore: number;
    attempts: number;
    locked: boolean;
    videoUrl: string | null; // null while the video is locked
}

export interface TrainingCertificate {
    id?: string;
    agentId?: string;
    courseId?: string;
    certificateNumber: string;
    score: number;
    issuedAt: string;
    course?: { id: string; title: string; passingScore?: number; [key: string]: unknown };
}

export interface TrainingCourseDetail {
    id: string;
    title: string;
    description?: string;
    passingScore: number;
    status: EnrollmentStatus | null;
    certified: boolean;
    certificate: TrainingCertificate | null;
    totalVideos: number;
    passedVideos: number;
    videos: TrainingVideo[];
}

export interface EnrollCourseResponse {
    status: EnrollmentStatus;
    startedAt: string;
}

export interface MarkWatchedResponse {
    watched: boolean;
    passed: boolean;
    certificate: TrainingCertificate | null;
}

export interface QuizOption {
    id: string;
    text: string;
    orderIndex: number;
}

export interface QuizQuestion {
    id: string;
    text: string;
    orderIndex: number;
    options: QuizOption[];
}

export interface VideoQuiz {
    videoId: string;
    title: string;
    passingScore: number;
    questions: QuizQuestion[];
}

export interface QuizAnswer {
    questionId: string;
    optionId: string;
}

export interface QuizResultItem {
    questionId: string;
    selectedOptionId: string | null;
    correct: boolean;
    correctOptionId: string;
}

export interface SubmitQuizResponse {
    score: number;
    passed: boolean;
    passingScore: number;
    correctCount: number;
    totalQuestions: number;
    results: QuizResultItem[];
    certificate: TrainingCertificate | null;
}

// ---- Admin authoring ----

export interface AdminTrainingOption {
    id?: string;
    text: string;
    isCorrect: boolean;
    orderIndex?: number;
}

export interface AdminTrainingQuestion {
    id: string;
    text: string;
    orderIndex: number;
    options: AdminTrainingOption[];
}

export interface AdminTrainingVideo {
    id: string;
    title: string;
    description?: string;
    sourceType: VideoSourceType;
    videoUrl: string | null;
    orderIndex: number;
    durationSeconds: number;
    isPublished: boolean;
    questions: AdminTrainingQuestion[];
}

export interface AdminTrainingCourse {
    id: string;
    title: string;
    description?: string;
    passingScore: number;
    isPublished: boolean;
    videos?: AdminTrainingVideo[];
    createdAt?: string;
}

export interface CreateCourseRequest {
    title: string;
    description?: string;
    passingScore?: number;
    isPublished?: boolean;
}

export type UpdateCourseRequest = Partial<CreateCourseRequest>;

export interface AddVideoLinkRequest {
    title: string;
    description?: string;
    videoUrl: string;
    orderIndex?: number;
    durationSeconds?: number;
    isPublished?: boolean;
}

export type UpdateVideoRequest = Partial<AddVideoLinkRequest>;

export interface AddQuestionRequest {
    text: string;
    orderIndex?: number;
    options: AdminTrainingOption[];
}

export interface UpdateQuestionRequest {
    text?: string;
    orderIndex?: number;
    options?: AdminTrainingOption[];
}

export interface DeletedResponse {
    deleted: boolean;
}

// ==================== CONTACT MESSAGES ====================

export type ContactMessageType = 'associate' | 'meeting' | 'general';
export type ContactMessageStatus = 'new' | 'read' | 'resolved';

export interface SubmitContactMessageRequest {
    name: string;
    email: string;
    phone?: string;
    message: string;
    type: ContactMessageType;
}

export interface ContactMessage {
    id: string;
    name: string;
    email: string;
    phone?: string | null;
    message: string;
    type: ContactMessageType;
    status: ContactMessageStatus;
    createdAt: string;
}

export interface UpdateContactMessageStatusRequest {
    id: string;
    status: ContactMessageStatus;
}

// ==================== PUBLIC VERIFICATION (QR) ====================

// Returned by GET /verify/:id for a verifiable field member (agent / medical /
// notary). A 404 means "not a recognized platform member". Fields are minimal by
// design — no email, phone, address or gender is exposed.
export interface VerificationResult {
    id: string;
    fullName: string;
    role: string;
    roleLabel: string;
    status: string;
    isActive: boolean;
    memberSince: string;
    verified: boolean;
}

// Only field roles carry a public verification QR — the endpoint 404s for
// everyone else, so the admin UI only offers the QR for these.
export const VERIFIABLE_ROLES: UserRole[] = [UserRole.AGENT, UserRole.MEDICAL, UserRole.NOTARY];

// ==================== EMPLOYER REFERENCE ====================

export type EmployerReferenceRating = 'excellent' | 'good' | 'bad';
export type EmployerReferenceStatus = 'pending' | 'responded';

// The reference a previous employer submits about the candidate.
export interface EmployerReferenceResponse {
    positionHeld?: string | null;
    periodOfEmployment?: string | null;
    workPerformance: EmployerReferenceRating;
    professionalConduct: EmployerReferenceRating;
    abilityToAdapt: EmployerReferenceRating;
    abilityToLearnAndImprove: EmployerReferenceRating;
    disciplinaryActions?: string | null;
    reasonForLeaving?: string | null;
    generalRemarks?: string | null;
}

// Public payload loaded by the employer via their token link.
export interface EmployerReferencePublic {
    employerName: string;
    declaredPosition?: string | null;
    employeeName: string;
    status: EmployerReferenceStatus;
    response: EmployerReferenceResponse | null;
}

// Body the employer submits (token is in the URL).
export interface SubmitEmployerReferenceRequest {
    token: string;
    positionHeld?: string;
    periodOfEmployment?: string;
    workPerformance: EmployerReferenceRating;
    professionalConduct: EmployerReferenceRating;
    abilityToAdapt: EmployerReferenceRating;
    abilityToLearnAndImprove: EmployerReferenceRating;
    disciplinaryActions?: string;
    reasonForLeaving?: string;
    generalRemarks?: string;
}

// Admin (Super Admin / CS) view of a reference request on an investigation.
export interface EmployerReference {
    id: string;
    investigationId: string;
    employerName: string;
    declaredPosition?: string | null;
    email?: string | null;
    phone?: string | null;
    status: EmployerReferenceStatus;
    token: string;
    link: string;
    response: EmployerReferenceResponse | null;
    createdAt: string;
    respondedAt?: string | null;
}

// ==================== BLOG ====================

export type BlogContentType = 'article' | 'document';
export type BlogStatus = 'draft' | 'published';

export interface BlogAuthor {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
}

export interface Blog {
    id: string;
    title: string;
    slug: string;
    excerpt: string | null;
    contentType: BlogContentType;
    body: string | null;
    documentUrl: string | null;
    documentName: string | null;
    documentMimeType: string | null;
    coverImageUrl: string | null;
    tags: string[] | null;
    status: BlogStatus;
    author: BlogAuthor | null;
    authorId: string | null;
    publishedAt: string | null;
    viewCount: number;
    createdAt: string;
    updatedAt: string;
}

// List endpoints omit the article `body`.
export type BlogListItem = Omit<Blog, 'body'>;

export interface PaginatedBlogs<T> {
    items: T[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export interface BlogListParams {
    page?: number;
    limit?: number;
    tag?: string;
    search?: string;
}

export interface CreateBlogRequest {
    title: string;
    contentType: BlogContentType;
    body?: string;
    excerpt?: string;
    tags?: string[];
    status?: BlogStatus;
}

export interface UpdateBlogRequest {
    title?: string;
    contentType?: BlogContentType;
    body?: string;
    excerpt?: string;
    tags?: string[];
    status?: BlogStatus;
}

// ==================== EMPLOYEE REPORTS (derogatory reports) ====================

export type EmployeeReportCategory =
    | 'fraud'
    | 'theft'
    | 'financial_misconduct'
    | 'falsification'
    | 'absconding'
    | 'violence'
    | 'harassment'
    | 'gross_misconduct'
    | 'other';

export type EmployeeReportSeverity = 'low' | 'medium' | 'high';

export type EmployeeReportStatus = 'active' | 'dismissed';

export interface EmployeeReport {
    id: string;
    employeeFullName: string;
    employeeEmail?: string | null;
    employeePhone?: string | null;
    employeeNin?: string | null;
    category: EmployeeReportCategory;
    severity: EmployeeReportSeverity;
    description: string;
    status: EmployeeReportStatus;
    reviewNotes?: string | null;
    investigationId?: string | null;
    createdAt: string;
    updatedAt?: string;
}

// A scan result carries `matchedOn` — which identifiers matched — so the UI can
// distinguish a strong match (nin/email/phone) from a weak name-only one.
export interface EmployeeReportScanMatch extends EmployeeReport {
    matchedOn: string[];
}

// POST /employee-reports body. Option A: pass `investigationId` (server pulls the
// verified identity incl. NIN). Option B: pass `employeeFullName` + at least one
// of email/phone/nin.
export interface FileEmployeeReportRequest {
    investigationId?: string;
    employeeFullName?: string;
    employeeEmail?: string;
    employeePhone?: string;
    employeeNin?: string;
    category: EmployeeReportCategory;
    severity?: EmployeeReportSeverity;
    description: string;
}

export interface EmployeeReportListParams {
    status?: EmployeeReportStatus | '';
    page?: number;
    limit?: number;
}

export interface EmployeeReportsResponse {
    data: EmployeeReport[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface EmployeeReportScanParams {
    name?: string;
    email?: string;
    phone?: string;
    nin?: string;
}

export interface UpdateEmployeeReportStatusRequest {
    id: string;
    status: EmployeeReportStatus;
    reviewNotes?: string;
}

// ==================== UTILITY TYPES ====================

export type Theme = 'light' | 'dark' | 'system';
export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
export type RequiredFields<T, K extends keyof T> = T & Required<Pick<T, K>>;
